// For more information on data-driven styles, see https://www.mapbox.com/help/gl-dds-ref/
import type {CircleLayer, FillLayer} from "react-map-gl";
import {LineLayer, StyleFunction, SymbolLayer} from "mapbox-gl";
import type {PaletteMode} from "@mui/material";

type ParkStop = [number, string];

// Light-mode palette: saturated colours that read well on the light basemap.
const parksStopsLight: ParkStop[] = [
  [0, '#006704'], [1, '#03c788'], [2, '#0067d2'], [3, '#cbbb00'],
  [5, '#e37200'], [10, '#d50024'], [100, '#000'], [101, '#667359'],
];

// Dark-mode palette: same hues as the light palette but brighter/lighter
// variants so the areas stay visible against the dark basemap.
const parksStopsDark: ParkStop[] = [
  [0, '#36c14a'], [1, '#3fe3ab'], [2, '#5aa9ff'], [3, '#e7d44a'],
  [5, '#ff9d45'], [10, '#ff5d76'], [100, '#9aa0a6'], [101, '#9aae8e'],
];

export function getParksColorStyleFunction(mode: PaletteMode = 'light'): StyleFunction {
  return {
    property: 'restriction',
    stops: mode === 'dark' ? parksStopsDark : parksStopsLight,
    default: mode === 'dark' ? '#9aa0a6' : '#000',
  };
}

export function getDataLayer(mode: PaletteMode = 'light'): FillLayer {
  return {
    id: 'data', type: 'fill', paint: {
      'fill-color': getParksColorStyleFunction(mode),
      // Slightly more opaque in dark mode so the fill reads against the dark base.
      'fill-opacity': mode === 'dark' ? 0.28 : 0.2
    }
  };
}

export function getDataLineLayer(mode: PaletteMode = 'light'): LineLayer {
  return {
    id: 'dataLine', type: 'line', paint: {
      'line-color': getParksColorStyleFunction(mode), 'line-opacity': 0.8, "line-width": ['interpolate', // Set the exponential rate of change to 0.5
        ['exponential', 0.5], ['zoom'], // When zoom is 10, buildings will be 100% transparent.
        14, 2, // When zoom is 18 or higher, buildings will be 100% opaque.
        18, 10]
    }
  };
}

export function getDataCircleLayer(mode: PaletteMode = 'light'): CircleLayer {
  return {
    'id': 'auckland-parks-circle',
    'type': 'circle',
    'source': 'auckland-parks',
    'filter': ['!=', 'cluster', true],
    'paint': {
      'circle-color': getParksColorStyleFunction(mode),
      'circle-opacity': 0.9,
      'circle-radius': 12
    }
  };
}

// Backwards-compatible light-mode defaults for callers that don't (yet) pass a mode.
export const parksColorStyleFunction: StyleFunction = getParksColorStyleFunction('light');
export const dataLayer: FillLayer = getDataLayer('light');
export const dataLineLayer: LineLayer = getDataLineLayer('light');
export const dataCircleLayer: CircleLayer = getDataCircleLayer('light');
